import { lazy } from 'react';
import { SimpleGrid, Box, Text, Group, useMantineTheme, ThemeIcon, Avatar, Badge } from '@mantine/core';
import {
  IconUsers,
  IconUserCheck,
  IconUserOff,
  IconUserExclamation,
  IconUserPause,
  IconBeach,
} from '@tabler/icons-react';
import { WidgetProps, UserStats as UserStatsType } from '../types';
import { registerWidget } from '../registry';
import { FadeIn } from '../../../animations';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

// ============================================================================
// UserStats Widget Component
// ============================================================================

function UserStatsWidget({ globalData, isCollapsed, settings }: WidgetProps) {
  const theme = useMantineTheme();
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  // Use computed text color if available (from container settings)
  const textColor = settings?.computedTextColor;
  const dimmedColor = textColor || 'dimmed';

  if (isCollapsed) return null;

  // Calculate user stats from global data
  const users = globalData?.users || [];
  const leaveRequests = globalData?.leaveRequests || [];

  const stats = {
    total: users.length,
    active: users.filter((u) => u.status === 'active').length,
    away: users.filter((u) => u.status === 'away').length,
    busy: users.filter((u) => u.status === 'busy').length,
    offline: users.filter((u) => u.status === 'offline').length,
    onLeave: leaveRequests.filter((r) => r.status === 'approved').length,
  };

  const statItems = [
    {
      label: 'Active',
      value: stats.active,
      icon: IconUserCheck,
      color: 'green',
    },
    {
      label: 'Away',
      value: stats.away,
      icon: IconUserPause,
      color: 'yellow',
    },
    {
      label: 'Busy',
      value: stats.busy,
      icon: IconUserExclamation,
      color: 'orange',
    },
    {
      label: 'Offline',
      value: stats.offline,
      icon: IconUserOff,
      color: 'gray',
    },
    {
      label: 'On Leave',
      value: stats.onLeave,
      icon: IconBeach,
      color: 'cyan',
    },
  ];

  // Get top 3 active users with avatars
  const activeUsers = users.filter((u) => u.status === 'active').slice(0, 3);

  return (
    <FadeIn>
      <Box>
        {/* Active Users Avatars */}
        <Group justify="center" mb="md">
          <Avatar.Group spacing="sm">
            {activeUsers.map((user) => (
              <Avatar
                key={user.id}
                src={user.avatar}
                alt={user.name}
                radius="xl"
                size="md"
                color="blue"
              >
                {user.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .slice(0, 2)}
              </Avatar>
            ))}
            {stats.active > 3 && (
              <Avatar radius="xl" size="md" color="gray">
                +{stats.active - 3}
              </Avatar>
            )}
          </Avatar.Group>
        </Group>

        {/* Stats Grid */}
        <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="xs">
          {statItems.map((item) => {
            const Icon = item.icon;
            return (
              <Box
                key={item.label}
                p="xs"
                style={{
                  backgroundColor: theme.colors[item.color][0],
                  borderRadius: theme.radius.sm,
                  textAlign: 'center',
                }}
              >
                <Group gap={4} justify="center" mb={2}>
                  <ThemeIcon size="xs" variant="transparent" color={item.color}>
                    <Icon size={12} />
                  </ThemeIcon>
                  <Text size="xs" c={dimmedColor} style={accessibilityStyles}>
                    {item.label}
                  </Text>
                </Group>
                <Text size="lg" fw={700} c={textColor || item.color} style={accessibilityStyles}>
                  {item.value}
                </Text>
              </Box>
            );
          })}
        </SimpleGrid>

        {/* Total */}
        <Box
          mt="sm"
          p="xs"
          style={{
            backgroundColor: theme.colors.gray[0],
            borderRadius: theme.radius.sm,
            textAlign: 'center',
          }}
        >
          <Group justify="center" gap="xs">
            <ThemeIcon size="sm" variant="light" color="blue">
              <IconUsers size={14} />
            </ThemeIcon>
            <Text size="xs" c={dimmedColor} style={accessibilityStyles}>
              Total Team Members
            </Text>
          </Group>
          <Text size="xl" fw={700} c={textColor} style={accessibilityStyles}>
            {stats.total}
          </Text>
        </Box>
      </Box>
    </FadeIn>
  );
}

// ============================================================================
// Widget Registration
// ============================================================================

registerWidget({
  id: 'user-stats',
  name: 'Team Statistics',
  description: 'Overview of team member status including active, away, busy, and on leave',
  icon: IconUsers,
  category: 'people',
  component: lazy(() => Promise.resolve({ default: UserStatsWidget })),
  sizes: {
    base: { cols: 12, rows: 5 },
    sm: { cols: 6, rows: 5 },
    md: { cols: 6, rows: 5 },
    lg: { cols: 4, rows: 5 },
    xl: { cols: 4, rows: 5 },
    tv: { cols: 3, rows: 5 },
  },
  sizeVariants: {
    variants: ['compact', 'standard', 'wide'],
    defaultVariant: 'standard',
    variantSizes: {
      compact: {
        base: { cols: 12, rows: 4 },
        sm: { cols: 6, rows: 4 },
        md: { cols: 4, rows: 4 },
        lg: { cols: 3, rows: 4 },
        xl: { cols: 3, rows: 4 },
        tv: { cols: 2, rows: 4 },
      },
      standard: {
        base: { cols: 12, rows: 5 },
        sm: { cols: 6, rows: 5 },
        md: { cols: 6, rows: 5 },
        lg: { cols: 4, rows: 5 },
        xl: { cols: 4, rows: 5 },
        tv: { cols: 3, rows: 5 },
      },
      wide: {
        base: { cols: 12, rows: 4 },
        sm: { cols: 12, rows: 4 },
        md: { cols: 8, rows: 4 },
        lg: { cols: 6, rows: 4 },
        xl: { cols: 6, rows: 4 },
        tv: { cols: 4, rows: 4 },
      },
      full: {
        base: { cols: 12, rows: 5 },
        sm: { cols: 12, rows: 5 },
        md: { cols: 12, rows: 5 },
        lg: { cols: 12, rows: 5 },
        xl: { cols: 12, rows: 5 },
        tv: { cols: 12, rows: 5 },
      },
    },
  },
  data: {
    sourceType: 'global',
    globalDataKeys: ['users', 'leaveRequests'],
  },
  hasSettings: true,
  expandable: true,
});

export default UserStatsWidget;
